"use client";

import { useEffect, useRef, useState } from "react";

// WordPress' in-frame script (wp-embed-template.js) never navigates on its own:
// it swallows link clicks and postMessages them out, and it reports the card's
// real height the same way. Without a listener on this side the card is stuck at
// a guessed height and every link inside it is dead. This is the parent half of
// that protocol (what wp-embed.js does on a normal WP site).
const WpEmbedFrame = ({ src, title, secret, fallbackHeight }) => {
  const frameRef = useRef(null);
  const [height, setHeight] = useState(fallbackHeight);

  useEffect(() => {
    const onMessage = (event) => {
      const frame = frameRef.current;
      // Only trust messages coming from this very iframe's window.
      if (!frame || !event.source || event.source !== frame.contentWindow) return;

      const data = event.data;
      if (!data || data.secret !== secret) return;

      if (data.message === "height") {
        const value = parseInt(data.value, 10);
        // Guard against the 0 the frame can report before layout settles.
        if (value > 0) setHeight(value);
        return;
      }

      if (data.message === "link") {
        let target;
        try {
          target = new URL(String(data.value), src);
        } catch {
          return;
        }
        // The frame is untrusted third-party content — never hand it a
        // javascript:/data: url to open.
        if (target.protocol !== "http:" && target.protocol !== "https:") return;
        window.open(target.toString(), "_blank", "noopener,noreferrer");
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [secret, src]);

  return (
    <iframe
      ref={frameRef}
      src={src}
      title={title}
      width="100%"
      height={height}
      style={{ display: "block", border: 0, height: `${height}px` }}
      loading="lazy"
      scrolling="no"
      sandbox="allow-scripts"
      referrerPolicy="strict-origin-when-cross-origin"
    />
  );
};

export default WpEmbedFrame;
